import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { HttpStatusCode, isAxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { logout as notifyBackendOfLogout } from '@/api/auth.api';
import { clearAccessToken } from '@/lib/auth/session';
import { ROUTES } from '@/lib/constants/routes';

/**
 * Ends the client session: clears the stored token, drops all cached server
 * state, and routes to the login screen.
 *
 * Deliberately does NOT touch the create-startup wizard draft
 * (`createStartupStore`): that store keys its drafts by user id
 * (`drafts[userId]`), so a shared/kiosk machine already never shows one
 * user's in-progress company name, financials, and founder data to a
 * different user who logs in afterward - each user only ever reads their own
 * entry. Wiping it here would defeat the point, which is that the SAME user
 * gets their draft back after logging back in. A draft is only cleared by
 * `reset(userId)` on a successful create (see `CreateStartupForm`).
 *
 * Also tells the backend (`POST /auth/logout`) so the logout can be audited
 * (MYN-146). That call is best-effort: it is started first, while the token is
 * still stored (`logout()` captures it synchronously), and the client-side
 * clear-and-navigate never waits on it. A failure (network, 500, or 401 - the
 * last also makes the `apiClient` interceptor clear the already-cleared token,
 * a harmless no-op) is never shown to the user. Network errors, 5xx and any
 * other status are reported to Sentry; a 401 is not (see the call site).
 *
 * BASELINE: the session JWT is stateless, so the backend call is for auditing
 * only and does NOT revoke the token. When the httpOnly-cookie + refresh-token
 * design lands (docs/specs/00-shared-contracts.md), the backend endpoint should also revoke;
 * callers of the returned function do not change.
 *
 * @returns a stable `logout` callback with no arguments.
 */
export function useLogout(): () => void {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useCallback(() => {
    notifyBackendOfLogout().catch((error: unknown) => {
      // WHY: a 401 here is an expected outcome, not a fault. Email replacement
      // (`ReplaceEmailDialog`) and account deletion (`DangerZoneCard`)
      // call logout after the Backend has already invalidated the session, and
      // MYN-146 lists "logout sent with an already-expired token" as
      // WONT-HANDLE (and a revoked session as HANDLE). Reporting it would be a
      // permanent, high-volume Sentry issue. The user still lands on login.
      if (isAxiosError(error) && error.response?.status === HttpStatusCode.Unauthorized) return;
      Sentry.captureException(error);
    });
    clearAccessToken();
    queryClient.clear();
    navigate(ROUTES.AUTH.LOGIN, { replace: true });
  }, [navigate, queryClient]);
}
